import React, { useState, useMemo } from 'react';
import { useFacturaCompra } from '../hooks/useFacturasCompra';
import ModalAnularFactura from './ModalAnularFactura';
import {
  X,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Receipt,
  Image as ImageIcon,
  Package,
} from 'lucide-react';
import type { FacturaCompra, DetalleFacturaCompra as DetalleType } from '../types/facturas';
import { formatearRut } from '../schemas/facturaSchema';
import { capitalizarIngrediente, capitalizarMarca } from '../utils/facturaFormatters';
import { formatearPrecio, formatearPrecioUnitario } from '../utils/facturaCalculos';
import { formatearFechaCon } from '../utils/dateUtils';

// ===================================
// HELPERS DE FORMATEO
// ===================================

/**
 * Formatea una cantidad siempre sin decimales (redondeada al entero más cercano).
 * Los valores en facturas de compra son cantidades enteras de unidades/kg/litros.
 */
const formatearCantidad = (cantidad: number): string => {
  return Math.round(cantidad).toLocaleString('es-CL');
};

const formatearFecha = (fecha: Date | string | undefined) => {
  if (!fecha) return 'No especificada';
  return formatearFechaCon(fecha, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatearFechaHora = (fecha: Date | string | undefined) => {
  if (!fecha) return 'No especificada';
  return new Date(fecha).toLocaleString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const metodoPagoLabel = (metodo: string) => {
  const metodos: Record<string, string> = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    cheque: 'Cheque',
    credito: 'Credito',
    debito: 'Debito',
  };
  return metodos[metodo] || metodo;
};

// ===================================
// CELDA DE CANTIDAD / PRESENTACION
// ===================================

interface CeldaCantidadProps {
  item: DetalleType;
}

const CeldaCantidad: React.FC<CeldaCantidadProps> = ({ item }) => {
  const nivel = item.jerarquia_niveles ?? (item.es_pack ? 2 : 1);
  // Prioridad: símbolo normalizado del JOIN con unidades_medida > unidad del ingrediente > valor del detalle
  const unidad = item.unidad_normalizada || item.ingrediente_unidad_medida || item.unidad_medida;

  // Nivel 3: tres niveles de jerarquia
  if (
    nivel === 3 &&
    item.nivel1_cantidad != null &&
    item.nivel1_nombre &&
    item.nivel1_unidades != null &&
    item.nivel2_nombre &&
    item.nivel2_cantidad_base != null
  ) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          {formatearCantidad(item.cantidad)} {unidad}
        </div>
        <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
          {item.descripcion_jerarquia ||
            `${formatearCantidad(item.nivel1_cantidad)} ${item.nivel1_nombre} × ${formatearCantidad(item.nivel1_unidades)} ${item.nivel2_nombre} × ${formatearCantidad(item.nivel2_cantidad_base)}${unidad}`}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">(Total calculado)</div>
      </div>
    );
  }

  // Nivel 2: pack / dos niveles
  if (nivel === 2 && item.nivel1_cantidad != null && item.nivel1_nombre && item.nivel1_unidades != null) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          {formatearCantidad(item.cantidad)} {unidad}
        </div>
        <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
          {item.descripcion_jerarquia ||
            `${formatearCantidad(item.nivel1_cantidad)} ${item.nivel1_nombre} × ${formatearCantidad(item.nivel1_unidades)} ${unidad}`}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">(Total calculado)</div>
      </div>
    );
  }

  // Pack legacy
  if (item.es_pack && item.cantidad_packs != null && item.unidades_por_pack != null) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          {formatearCantidad(item.cantidad)} {unidad}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {item.formato_compra || `${formatearCantidad(item.cantidad_packs)} packs × ${formatearCantidad(item.unidades_por_pack)} ${unidad}`}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">(Total calculado)</div>
      </div>
    );
  }

  // Simple
  return (
    <div className="text-right text-gray-900 dark:text-gray-100">
      {formatearCantidad(item.cantidad)} {unidad}
    </div>
  );
};

// ===================================
// CELDA DE PRECIO
// ===================================

interface CeldaPrecioProps {
  item: DetalleType;
}

const CeldaPrecio: React.FC<CeldaPrecioProps> = ({ item }) => {
  const nivel = item.jerarquia_niveles ?? (item.es_pack ? 2 : 1);
  // Prioridad: símbolo normalizado del JOIN con unidades_medida > unidad del ingrediente > valor del detalle
  const unidad = item.unidad_normalizada || item.ingrediente_unidad_medida || item.unidad_medida;

  if (nivel === 3 && item.precio_nivel1 != null) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          ${formatearPrecioUnitario(item.precio_unitario)}/{unidad}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          ${formatearPrecioUnitario(item.precio_nivel1)}/{item.nivel1_nombre || 'caja'}
        </div>
        {item.precio_nivel2 != null && item.nivel2_nombre && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ${formatearPrecioUnitario(item.precio_nivel2)}/{item.nivel2_nombre}
          </div>
        )}
      </div>
    );
  }

  if (nivel === 2 && item.precio_nivel1 != null) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          ${formatearPrecioUnitario(item.precio_unitario)}/{unidad}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          ${formatearPrecioUnitario(item.precio_nivel1)}/{item.nivel1_nombre || 'pack'}
        </div>
      </div>
    );
  }

  if (item.es_pack && item.precio_pack != null) {
    return (
      <div className="text-right">
        <div className="text-gray-900 dark:text-gray-100">
          ${formatearPrecioUnitario(item.precio_unitario)}/{unidad}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Pack: ${formatearPrecioUnitario(item.precio_pack)}
        </div>
      </div>
    );
  }

  return (
    <div className="text-right text-gray-900 dark:text-gray-100">
      ${formatearPrecioUnitario(item.precio_unitario)}/{unidad}
    </div>
  );
};

// ===================================
// PROPS DEL COMPONENTE PRINCIPAL
// ===================================

interface DetalleFacturaCompraProps {
  isOpen: boolean;
  onClose: () => void;
  facturaId: number;
  onEdit?: (factura: FacturaCompra) => void;
  onAnular?: (facturaId: number) => void;
}

// ===================================
// COMPONENTE PRINCIPAL
// ===================================

const DetalleFacturaCompra: React.FC<DetalleFacturaCompraProps> = ({
  isOpen,
  onClose,
  facturaId,
  onEdit,
  onAnular,
}) => {
  const [showModalAnular, setShowModalAnular] = useState(false);
  const [isAnulando, setIsAnulando] = useState(false);
  const {
    data: response,
    isLoading,
    error,
  } = useFacturaCompra(facturaId, isOpen);

  const handleConfirmarAnular = async () => {
    if (!onAnular) return;
    setIsAnulando(true);
    try {
      await onAnular(facturaId);
      setShowModalAnular(false);
    } catch (error) {
      console.error('Error al anular factura:', error);
    } finally {
      setIsAnulando(false);
    }
  };

  const estadoBadgeClasses = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-200 dark:border-yellow-800';
      case 'registrada':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800';
      case 'anulada':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600';
    }
  };

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return <Edit className="h-3.5 w-3.5" />;
      case 'registrada':
        return <CheckCircle className="h-3.5 w-3.5" />;
      case 'anulada':
        return <XCircle className="h-3.5 w-3.5" />;
      default:
        return <AlertTriangle className="h-3.5 w-3.5" />;
    }
  };

  // RUT formateado con memoizacion
  const rutFormateado = useMemo(() => {
    if (!response?.data?.rut_proveedor) return '';
    try {
      return formatearRut(response.data.rut_proveedor);
    } catch {
      return response.data.rut_proveedor;
    }
  }, [response?.data?.rut_proveedor]);

  if (!isOpen) return null;

  // Estado de carga
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-white">
              Cargando detalles...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertTriangle className="h-6 w-6" />
            <h3 className="text-lg font-semibold">Error al cargar</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            No se pudieron cargar los detalles de la factura.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  const factura = response?.data;
  if (!factura) return null;

  const estadoLabel =
    factura.estado
      ? factura.estado.charAt(0).toUpperCase() + factura.estado.slice(1)
      : 'Sin estado';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">

        {/* ── HEADER ── */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 z-10">
          <div className="flex items-start justify-between gap-4">
            {/* Titulo + estado */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-gray-400" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Factura {factura.numero_factura}
                </h2>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${estadoBadgeClasses(factura.estado)}`}
              >
                {estadoIcon(factura.estado)}
                {estadoLabel}
              </span>
            </div>

            {/* Acciones + cerrar */}
            <div className="flex items-center gap-2 shrink-0">
              {factura.estado === 'borrador' && onEdit && (
                <button
                  onClick={() => onEdit(factura)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  <Edit className="h-4 w-4 mr-1.5" />
                  Editar
                </button>
              )}
              {factura.estado !== 'anulada' && onAnular && (
                <button
                  onClick={() => setShowModalAnular(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                >
                  <XCircle className="h-4 w-4 mr-1.5" />
                  Anular
                </button>
              )}
              {factura.imagen_url && (
                <a
                  href={factura.imagen_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  <ImageIcon className="h-4 w-4 mr-1.5" />
                  Ver imagen
                </a>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
                aria-label="Cerrar"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* ── CONTENIDO ── */}
        <div className="p-6 space-y-4">

          {/* ── TARJETA 1: Datos de la Factura ── */}
          <div
            className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
            role="region"
            aria-labelledby="detalle-datos-factura"
          >
            <h3
              id="detalle-datos-factura"
              className="font-medium mb-3 text-gray-900 dark:text-gray-100"
            >
              Datos de la Factura
            </h3>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Numero:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                  {factura.numero_factura}
                </span>
              </div>

              <div>
                <span className="text-gray-600 dark:text-gray-400">Fecha emision:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                  {formatearFecha(factura.fecha_emision)}
                </span>
              </div>

              <div>
                <span className="text-gray-600 dark:text-gray-400">Proveedor:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                  {factura.nombre_proveedor}
                </span>
              </div>

              <div>
                <span className="text-gray-600 dark:text-gray-400">RUT:</span>
                <span className="ml-2 font-medium font-mono text-gray-900 dark:text-gray-100">
                  {rutFormateado || factura.rut_proveedor}
                </span>
              </div>

              <div>
                <span className="text-gray-600 dark:text-gray-400">Metodo de pago:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
                  {metodoPagoLabel(factura.metodo_pago)}
                </span>
              </div>

              {factura.numero_orden && (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Orden de compra:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {factura.numero_orden}
                  </span>
                </div>
              )}

              {factura.proveedor_email && (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Email proveedor:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {factura.proveedor_email}
                  </span>
                </div>
              )}

              {factura.proveedor_telefono && (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Telefono proveedor:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {factura.proveedor_telefono}
                  </span>
                </div>
              )}

              {factura.usuario_nombre && (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Registrado por:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                    {factura.usuario_nombre}
                  </span>
                </div>
              )}

              <div>
                <span className="text-gray-600 dark:text-gray-400">Fecha registro:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                  {formatearFecha(factura.fecha_registro)}
                </span>
              </div>
            </div>
          </div>

          {/* ── TARJETA 2: Tabla de ingredientes ── */}
          {factura.detalles && factura.detalles.length > 0 && (
            <div
              className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden"
              role="region"
              aria-labelledby="detalle-ingredientes"
            >
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th
                      id="detalle-ingredientes"
                      className="px-4 py-2 text-left text-gray-700 dark:text-gray-300"
                    >
                      Ingrediente
                    </th>
                    <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">
                      Marca
                    </th>
                    <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">
                      Categoria
                    </th>
                    <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      Cantidad
                    </th>
                    <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      Valor unitario
                    </th>
                    <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {factura.detalles.map((item, index) => {
                    const nivel = item.jerarquia_niveles ?? (item.es_pack ? 2 : 1);
                    const tieneJerarquia = nivel >= 2;

                    return (
                      <tr
                        key={item.id || index}
                        className="border-t border-gray-200 dark:border-gray-700"
                      >
                        {/* Ingrediente */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900 dark:text-gray-100">
                              {capitalizarIngrediente(
                                item.ingrediente_nombre ||
                                  item.nombre_ingrediente_temporal
                              )}
                            </span>
                            {tieneJerarquia && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                                           bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                              >
                                <Package className="w-3 h-3" />
                                {nivel === 3 ? 'Jerarquia' : 'Pack'}
                              </span>
                            )}
                            {item.ingrediente_creado_automaticamente === true && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                Nuevo
                              </span>
                            )}
                          </div>
                          {tieneJerarquia && item.descripcion_jerarquia && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              {item.descripcion_jerarquia}
                            </div>
                          )}
                          {!tieneJerarquia && item.es_pack && item.formato_compra && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {item.formato_compra}
                            </div>
                          )}
                        </td>

                        {/* Marca */}
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                          {capitalizarMarca(
                            (item as any).ingrediente_marca || item.marca
                          )}
                        </td>

                        {/* Categoria */}
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                          {item.categoria || item.categoria_nombre || 'Otros'}
                        </td>

                        {/* Cantidad */}
                        <td className="px-4 py-2">
                          <CeldaCantidad item={item} />
                        </td>

                        {/* Precio */}
                        <td className="px-4 py-2">
                          <CeldaPrecio item={item} />
                        </td>

                        {/* Subtotal */}
                        <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                          ${formatearPrecio(item.subtotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* Footer con totales — idéntico al WizardStep3 */}
                <tfoot className="bg-gray-50 dark:bg-gray-800 font-medium">
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td
                      colSpan={5}
                      className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
                    >
                      Subtotal (sin IVA):
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                      ${formatearPrecio(factura.subtotal)}
                    </td>
                  </tr>
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td
                      colSpan={5}
                      className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
                    >
                      IVA (19%):
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                      ${formatearPrecio(factura.iva)}
                    </td>
                  </tr>
                  <tr className="text-lg border-t border-gray-200 dark:border-gray-700">
                    <td
                      colSpan={5}
                      className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
                    >
                      Total:
                    </td>
                    <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400">
                      ${formatearPrecio(factura.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* ── TARJETA 3: Observaciones (si existen) ── */}
          {factura.observaciones && (
            <div
              className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
              role="region"
            >
              <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
                Observaciones
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {factura.observaciones}
              </p>
            </div>
          )}

          {/* ── Metadatos del sistema ── */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-400 dark:text-gray-500">
              <div>
                <span className="font-medium">Creado:</span>{' '}
                {formatearFechaHora(factura.created_at)}
              </div>
              <div>
                <span className="font-medium">Actualizado:</span>{' '}
                {formatearFechaHora(factura.updated_at)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmacion para anular */}
      {factura && (
        <ModalAnularFactura
          isOpen={showModalAnular}
          onClose={() => setShowModalAnular(false)}
          onConfirm={handleConfirmarAnular}
          numeroFactura={factura.numero_factura}
          totalItems={factura.detalles?.length || 0}
          isLoading={isAnulando}
        />
      )}
    </div>
  );
};

export default DetalleFacturaCompra;
